import {
  AbutmentTypeEnum,
  isPartialStandardAbutmentCharacteristics,
  isValidAbutmentType,
  type AbutmentCharacteristics,
  type AbutmentType,
  type StandardAbutmentCharacteristics,
} from "@jasper/client-models";
import { Input, Stack } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { FieldValueProps } from "../../types";
import { ButtonSelect } from "../../components";
import { handleDivBlur } from "../../utils";

type Props = FieldValueProps<AbutmentCharacteristics>;

const getGlobalAbutmentType = (abutmentType?: AbutmentType) => {
  if (!abutmentType) {
    return undefined;
  }

  if (abutmentType === AbutmentTypeEnum.CUSTOM) {
    return AbutmentTypeEnum.CUSTOM;
  }

  return AbutmentTypeEnum.STANDARD;
};

const StandardAbutmentCharacteristicsForm = ({
  value,
  errors,
  onChange,
}: FieldValueProps<StandardAbutmentCharacteristics>) => {
  const { t } = useTranslation("forms", {
    keyPrefix: "forms.abutmentCharacteristics",
  });

  const handleStandardAbutmentTypeChange = (nextAbutmentType?: string) => {
    if (!nextAbutmentType || !isValidAbutmentType(nextAbutmentType)) {
      return;
    }

    onChange({
      ...value,
      abutmentType: nextAbutmentType,
    });
  };

  return (
    <>
      <ButtonSelect
        data={[
          {
            value: AbutmentTypeEnum.STANDARD,
            label: t("choices.standardOriginal"),
          },
          {
            value: AbutmentTypeEnum.STANDARD_GENERIC,
            label: t("choices.standardGeneric"),
          },
        ]}
        value={value?.abutmentType}
        label={t("labels.standardAbutmentType")}
        onChange={handleStandardAbutmentTypeChange}
      />
      <Input.Wrapper
        label={t("labels.abutmentPreferences")}
        description={t("descriptions.abutmentPreferences")}
        error={
          errors?.abutmentPreferences
            ? t("errors.abutmentPreferences")
            : undefined
        }
      >
        <Input
          value={value?.abutmentPreferences ?? ""}
          onChange={e =>
            onChange({
              ...value,
              abutmentPreferences: e.target.value || undefined,
            })
          }
          error={
            errors?.abutmentPreferences
              ? t("errors.abutmentPreferences")
              : undefined
          }
        />
      </Input.Wrapper>
    </>
  );
};

export const AbutmentCharacteristicsForm = ({
  value,
  errors = {},
  onChange,
  onBlur,
}: Props) => {
  const { t } = useTranslation("forms", {
    keyPrefix: "forms.abutmentCharacteristics",
  });

  const handleAbutmentTypeChange = (nextAbutmentType?: string) => {
    if (!nextAbutmentType || !isValidAbutmentType(nextAbutmentType)) {
      return;
    }

    if (
      getGlobalAbutmentType(nextAbutmentType) ===
      getGlobalAbutmentType(value?.abutmentType)
    ) {
      return;
    }

    onChange({
      ...value,
      abutmentType: nextAbutmentType,
      abutmentPreferences:
        nextAbutmentType === AbutmentTypeEnum.CUSTOM
          ? undefined
          : (value as StandardAbutmentCharacteristics)?.abutmentPreferences, // Custom abutment does not have abutmentPreferences
    });
  };

  return (
    <Stack gap="md" onBlur={event => handleDivBlur(event, onBlur)}>
      <ButtonSelect
        data={[
          { value: AbutmentTypeEnum.STANDARD, label: t("choices.standard") },
          { value: AbutmentTypeEnum.CUSTOM, label: t("choices.custom") },
        ]}
        value={getGlobalAbutmentType(value?.abutmentType)}
        label={t("labels.abutmentType")}
        onChange={handleAbutmentTypeChange}
        error={errors?.abutmentType ? t("errors.abutmentType") : undefined}
      />

      {isPartialStandardAbutmentCharacteristics(value ?? {}) && (
        <StandardAbutmentCharacteristicsForm
          value={value}
          errors={errors}
          onChange={onChange}
        />
      )}
    </Stack>
  );
};
